import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useGetCurrentUserResidentsQuery } from "@redux/residents/residentsApi";
import { mapResidentInsuranceAdoptionStatus } from "@redux/residents/utils";

import {
  GetCurrentUserResidentsQuery,
  InsuranceAdoptionStatus,
} from "@graphql/api";
import { featureFlags } from "@utils/featureFlags";
import { mapSiteAddress } from "@utils";

import LoadingOverlay from "@covertree/ui/loading/LoadingOverlay";
import ErrorContent from "@covertree/ui/content/ErrorContent";
import DisplayPropertiesTableWithBackground from "@covertree/ui/content/DisplayPropertiesTableWithBackground";

import YourDetails from "./YourDetails";
import UploadProofOfCoverage from "./UploadProofOfCoverage";

type ResidentData =
  GetCurrentUserResidentsQuery["getCurrentUserResidents"][number];

function canUploadProofOfCoverage(resident: ResidentData) {
  return (
    resident.insuranceAdoption.status !== InsuranceAdoptionStatus.Excluded &&
    featureFlags.uploadProofOfCoverageToResident.isEnabled()
  );
}

function ResidentStatusAndDetails({ resident }: { resident: ResidentData }) {
  return (
    <Box>
      <Box mb={4}>
        <DisplayPropertiesTableWithBackground
          properties={[
            {
              label: "Insurance Adoption Status",
              value: mapResidentInsuranceAdoptionStatus(
                resident.insuranceAdoption.status
              ),
            },
          ]}
          wideValues
        />
      </Box>
      <YourDetails data={resident} />
    </Box>
  );
}

export default function DashboardPageContent() {
  const { data, isLoading, isError } = useGetCurrentUserResidentsQuery();

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (!data || isError) {
    return (
      <Box py={6}>
        <ErrorContent>
          Oops! <br />
          Something went wrong while loading your data. <br />
          Please try again later.
        </ErrorContent>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        py={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={1}>
        <Typography
          variant="subtitle1"
          align="center"
          color="#4F4F4F"
          sx={{ lineHeight: "1.5em" }}>
          There are no residents linked to your account yet.
        </Typography>
      </Box>
    );
  }

  const [firstResident] = data;

  if (data.length === 1) {
    return (
      <Box pb={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            marginBottom: {
              xs: 5,
              sm: 6.5,
            },
            marginTop: {
              md: 8,
            },
          }}>
          <Typography variant="h3">Hello {firstResident.firstName}!</Typography>
          {canUploadProofOfCoverage(firstResident) && (
            <UploadProofOfCoverage residentId={firstResident.id} />
          )}
        </Stack>
        <ResidentStatusAndDetails resident={firstResident} />
      </Box>
    );
  }

  return (
    <Box pb={2}>
      <Stack
        sx={{
          marginBottom: {
            xs: 5,
            sm: 6.5,
          },
          marginTop: {
            md: 8,
          },
        }}>
        <Typography variant="h3">Hello {firstResident.firstName}!</Typography>
      </Stack>
      <Stack spacing={6} divider={<Divider flexItem />}>
        {data.map((resident) => (
          <Box key={resident.id}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={3}>
              <Box>
                <Typography variant="h4">
                  {resident.firstName} {resident.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mapSiteAddress(resident.siteAddress)}
                </Typography>
              </Box>
              {canUploadProofOfCoverage(resident) && (
                <UploadProofOfCoverage residentId={resident.id} />
              )}
            </Stack>
            <ResidentStatusAndDetails resident={resident} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
